<template>
    <DsCard class="client_contact_container mb-3 text-content-primary">
        <header class="mb-2 flex h-8 items-center justify-between">
            <h3 class="client-heading text-content-md font-semibold">Client</h3>
            <DsIconButton
                v-if="isRemovable"
                :disabled="addFileStore.isFormDisabled"
                icon="fa-regular fa-trash-can"
                size="sm"
                tooltipText="Remove client"
                @click="$emit('removeClient')"
            />
        </header>
        <ClientFormFields
            v-model:client="client"
            class="grid grid-cols-12 gap-x-3"
            :clientAttrs
            :clientErrors="fileClientStore.clientInfoErrors.get(clientKey) ?? createEmptyErrors()"
            :disabled="addFileStore.isFormDisabled"
            :fieldActions="clientFieldActions"
            @fieldActionClick="fieldActionHandler.onActionClick"
            @fieldInput="clearFieldError"
        />
    </DsCard>
</template>

<script setup lang="ts">
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import type {
    ActionForClientField,
    AddFileClient,
    AddFileClientKey,
    AddFileFieldAction,
} from '@/company-portal/add-file/add-file.types';
import { FileFieldActionScope } from '@/company-portal/add-file/add-file.types';
import type { ClientAttrs, ClientValidationErrorKeys } from '@/components/Views/Company/File/Client/client-info-form.types';
import { createEmptyErrors } from '@/components/Views/Company/File/Client/client-info-form.validation';
import ClientFormFields from '@/components/Views/Company/File/Client/ClientFormFields.vue';
import { FIELD_ACTIONS_INJECTION_KEY } from '@/injection-keys';
import { useFileClientStore } from '@/stores/company/file-client.store';
import DsIconButton from '@ds/button/DsIconButton.vue';
import DsCard from '@ds/DsCard.vue';
import { computed, inject } from 'vue';

export interface ClientFormCardProps {
    clientKey: AddFileClientKey;
    isRemovable?: boolean;
}

const props = defineProps<ClientFormCardProps>();

defineEmits<{
    removeClient: [];
}>();

const client = defineModel<AddFileClient>('client', { required: true });

const addFileStore = useAddFileStore();
const fileClientStore = useFileClientStore();
const fieldActionHandler = inject(FIELD_ACTIONS_INJECTION_KEY, { onActionClick() {} });

const clientFieldActions = computed(() =>
    addFileStore.fieldActions.reduce<Partial<Record<ActionForClientField, AddFileFieldAction>>>((actions, action) => {
        if (action.scope === FileFieldActionScope.CLIENT && action.clientId === props.clientKey) {
            actions[action.field] = action;
        }
        return actions;
    }, {}),
);

const clearFieldError = (field: ClientValidationErrorKeys) => {
    const clientError = fileClientStore.clientInfoErrors.get(props.clientKey);
    if (clientError) {
        clientError[field] = '';
    }
};

const clientAttrs: ClientAttrs = {
    first: {
        class: 'client-first col-span-12 md:col-span-6 md:col-start-1',
    },
    last: {
        class: 'client-last col-span-12 md:col-span-6',
    },
    email: {
        class: 'client-email col-span-12',
    },
    phone: {
        class: 'client-phone col-span-12 md:col-span-6',
    },
    role: {
        class: 'col-span-12 md:col-span-6 xl:col-span-4',
    },
};
</script>

<style scoped>
/* appends a number after "Client" in the heading (e.g. "Client 1", "Client 2", etc.) */
h3::after {
    counter-increment: client-heading;
    content: ' ' counter(client-heading);
}
</style>
